import { useRef, useState, type FormEvent } from 'react';
import {
  Banner,
  Button,
  Dialog,
  MenuItem,
  MenuList,
  Popover,
  Sheet,
  TextField,
  useToast,
} from '../../../ds/components';
import { useIsMobile } from '../../../ds/hooks';
import type { MeetingView } from '../api';
import { formatClock, secondsSince, shortPersonName, useNow } from '../clock';
import type { BrimoRoomParticipant } from '../room-client';
import { HeaderPill, HeaderTitle, RoomHeader } from './RoomHeader';
import {
  MarkersPanel,
  NoteForm,
  Sidebar,
  canMark,
  markedLabel,
  useMarkers,
  type SidebarTab,
} from './Sidebar';
import {
  ConversationPill,
  PillControl,
  RecordingPill,
  RemoteAudio,
  RoundControl,
  TrackVideo,
  VideoTile,
} from './parts';
import styles from './CallView.module.css';

export interface CallConnection {
  state: 'connecting' | 'connected' | 'failed';
  error: string | null;
}

export interface CallViewProps {
  meeting: MeetingView;
  hostName: string;
  participants: BrimoRoomParticipant[];
  connection: CallConnection;
  onReconnect: () => void;
  micOn: boolean;
  camOn: boolean;
  onToggleMic: () => void;
  onToggleCam: () => void;
  /** The label of the running screen share („Grundriss WE 2.05"), null without a share. */
  shareLabel: string | null;
  /** Starts (or switches) the screen share under `label`; resolves false when the person cancelled the picker. */
  onShare: (label: string) => Promise<boolean>;
  onStopShare: () => Promise<void>;
  onEnd: () => Promise<void>;
  ending: boolean;
}

/**
 * Beratung 11/12 (desktop) and 14 (below 768 px): the running conversation — its clock (the recording pill
 * when the meeting records, „Gespräch · 24:18" otherwise), gallery or shared screen with thumbnails, the
 * control bar, and the context sidebar (bottom sheets on the phone).
 */
export function CallView(props: CallViewProps) {
  const mobile = useIsMobile();
  return mobile ? <MobileCall {...props} /> : <DesktopCall {...props} />;
}

function useCallFacts(props: CallViewProps) {
  const now = useNow(1000);
  // the conversation start is the clock in both modes (the recording starts with it)
  const seconds = secondsSince(props.meeting.startedAt, now);
  const remote = props.participants.filter((participant) => !participant.isLocal);
  const local = props.participants.find((participant) => participant.isLocal) ?? null;
  const guest = props.meeting.participants.find((participant) => participant.type === 'guest') ?? null;
  const deal = props.meeting.context.deal;
  const dealShort = deal ? `Deal #${deal.number}` : null;
  const clockSeconds = () => secondsSince(props.meeting.startedAt, Date.now());
  return {
    seconds,
    recording: props.meeting.recording.enabled,
    remote,
    primary: remote[0] ?? null,
    local,
    guestName: guest?.name ?? props.meeting.appointment.contactName ?? 'Gast',
    dealShort,
    dealLine: deal ? `Deal #${deal.number} · ${deal.stage}` : props.meeting.appointment.dealLine,
    clockSeconds,
    audioTracks: remote.flatMap((participant) => (participant.audioTrack ? [participant.audioTrack] : [])),
  };
}

/** Screen-reader clock: the time every 10 seconds, not every second. */
function ClockAnnouncement({ seconds, recording }: { seconds: number; recording: boolean }) {
  return (
    <span className={styles.srOnly} aria-live="polite">
      {recording ? 'Aufzeichnung' : 'Gesprächsdauer'} {formatClock(Math.floor(seconds / 10) * 10)}
    </span>
  );
}

/** The head row's clock: „● Aufzeichnung · 24:18" when the meeting records, else „Gespräch · 24:18". */
function ClockPill({ seconds, recording }: { seconds: number; recording: boolean }) {
  return recording ? (
    <RecordingPill>Aufzeichnung · {formatClock(seconds)}</RecordingPill>
  ) : (
    <ConversationPill>Gespräch · {formatClock(seconds)}</ConversationPill>
  );
}

function ConnectionNotice({
  connection,
  onReconnect,
}: {
  connection: CallConnection;
  onReconnect: () => void;
}) {
  if (connection.state !== 'failed') return null;
  return (
    <Banner
      tone="warning"
      className={styles.notice}
      actions={
        <Button size="small" variant="secondary" onClick={onReconnect}>
          Erneut verbinden
        </Button>
      }
    >
      {connection.error ?? 'Die Verbindung zum Meetingraum ist unterbrochen.'}
    </Banner>
  );
}

function DesktopCall(props: CallViewProps) {
  const { meeting, hostName, connection } = props;
  const facts = useCallFacts(props);
  const [tab, setTab] = useState<SidebarTab>('deal');
  const sharing = props.shareLabel !== null;
  const title = `${meeting.appointment.eventTypeName} · ${shortPersonName(facts.guestName)}`;
  const count = Math.max(1, props.participants.length);

  return (
    <div className={styles.page}>
      <RemoteAudio tracks={facts.audioTracks} />
      <ClockAnnouncement seconds={facts.seconds} recording={facts.recording} />
      <RoomHeader
        variant="call"
        end={
          sharing ? 'Freigabe für den Mandanten sichtbar · Exposé und Preisliste gesperrt' : facts.dealLine
        }
      >
        <ClockPill seconds={facts.seconds} recording={facts.recording} />
        {sharing ? (
          <>
            <h1 className={styles.srOnly}>{title}</h1>
            <HeaderPill tone="share">Sie teilen: {props.shareLabel}</HeaderPill>
          </>
        ) : (
          <>
            <HeaderTitle plain>{title}</HeaderTitle>
            <HeaderPill>Einwilligung vollständig · {count} Teilnehmende</HeaderPill>
          </>
        )}
      </RoomHeader>
      <div className={styles.body}>
        <main className={styles.main}>
          <ConnectionNotice connection={connection} onReconnect={props.onReconnect} />
          {sharing ? (
            <>
              <div className={styles.shareStage}>
                <TrackVideo track={facts.local?.screenTrack ?? null} fit="contain" />
                <span className={styles.shareLabel}>{props.shareLabel}</span>
              </div>
              <div className={styles.shareRow}>
                <VideoTile
                  className={styles.thumb}
                  size="small"
                  track={facts.primary?.videoTrack ?? null}
                  name={shortPersonName(facts.primary?.name ?? facts.guestName)}
                  active={facts.primary?.speaking ?? false}
                />
                <VideoTile
                  className={styles.thumb}
                  size="small"
                  track={facts.local?.videoTrack ?? null}
                  mirrored
                  name="Sie"
                  active={facts.local?.speaking === true && facts.primary?.speaking !== true}
                />
                <div className={styles.shareActions}>
                  <MicCam {...props} />
                  <ShareButton label="Freigabe wechseln" tone="glass" onShare={props.onShare} compact />
                  <PillControl compact onClick={() => void props.onStopShare()}>
                    Freigabe beenden
                  </PillControl>
                  <EndButton onEnd={props.onEnd} ending={props.ending} recording={facts.recording} compact />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles.gallery}>
                <VideoTile
                  track={facts.primary?.videoTrack ?? null}
                  name={
                    facts.primary
                      ? `${facts.primary.name}${facts.primary.speaking ? ' · spricht' : ''}`
                      : facts.guestName
                  }
                  active={facts.primary?.speaking ?? false}
                  placeholder={
                    connection.state === 'connecting'
                      ? 'Verbindung wird aufgebaut …'
                      : facts.primary
                        ? null
                        : `${facts.guestName} ist noch nicht im Gespräch.`
                  }
                />
                <VideoTile
                  track={facts.local?.videoTrack ?? null}
                  mirrored
                  name={`${hostName} · Sie`}
                  active={facts.local?.speaking === true && facts.primary?.speaking !== true}
                  placeholder={props.camOn ? null : 'Kamera aus'}
                />
              </div>
              <div className={styles.controls} role="toolbar" aria-label="Gespräch">
                <MicCam {...props} />
                <ShareButton label="Exposé teilen" tone="accent" onShare={props.onShare} />
                <MoreMenu meeting={meeting} onTranscript={() => setTab('transcript')} />
                <EndButton onEnd={props.onEnd} ending={props.ending} recording={facts.recording} />
              </div>
            </>
          )}
        </main>
        <Sidebar meeting={meeting} tab={tab} onTab={setTab} clockSeconds={facts.clockSeconds} />
      </div>
    </div>
  );
}

function MicCam({
  micOn,
  camOn,
  onToggleMic,
  onToggleCam,
  connection,
  large = false,
}: CallViewProps & { large?: boolean }) {
  const disabled = connection.state !== 'connected';
  return (
    <>
      <RoundControl
        label="Mic"
        ariaLabel="Mic"
        pressed={micOn}
        tone={micOn ? 'glass' : 'off'}
        size={large ? 'large' : 'default'}
        disabled={disabled}
        onClick={onToggleMic}
      />
      <RoundControl
        label="Cam"
        ariaLabel="Cam"
        pressed={camOn}
        tone={camOn ? 'glass' : 'off'}
        size={large ? 'large' : 'default'}
        disabled={disabled}
        onClick={onToggleCam}
      />
    </>
  );
}

/** „Exposé teilen" / „Freigabe wechseln": asks for the label shown in the head row, then opens the picker. */
function ShareButton({
  label,
  tone,
  onShare,
  compact = false,
  round = false,
}: {
  label: string;
  tone: 'glass' | 'accent';
  onShare: (label: string) => Promise<boolean>;
  compact?: boolean;
  round?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('Bildschirm');
  const [busy, setBusy] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const { toast } = useToast();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await onShare(name.trim() || 'Bildschirm');
      setOpen(false);
    } catch {
      toast({ message: 'Die Freigabe konnte nicht gestartet werden.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {round ? (
        <RoundControl
          label="Teilen"
          tone="accent"
          size="large"
          buttonRef={anchor}
          expanded={open}
          hasPopup="dialog"
          onClick={() => setOpen((value) => !value)}
        />
      ) : (
        <PillControl
          tone={tone}
          compact={compact}
          buttonRef={anchor}
          expanded={open}
          hasPopup="dialog"
          onClick={() => setOpen((value) => !value)}
        >
          {label}
        </PillControl>
      )}
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        placement="top-start"
        variant="panel"
        width={300}
        label="Bildschirm teilen"
        initialFocus="first"
      >
        <form className={styles.shareForm} onSubmit={(event) => void submit(event)}>
          <TextField
            label="Was teilen Sie?"
            hint="Erscheint in der Kopfzeile als „Sie teilen: …“."
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
          />
          <div className={styles.shareFormActions}>
            <Button variant="secondary" size="small" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" size="small" loading={busy}>
              Teilen
            </Button>
          </div>
        </form>
      </Popover>
    </>
  );
}

function MoreMenu({ meeting, onTranscript }: { meeting: MeetingView; onTranscript: () => void }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const copy = useCopyGuestLink(meeting.guestUrl);
  return (
    <>
      <RoundControl
        label="···"
        ariaLabel="Weitere Aktionen"
        buttonRef={anchor}
        expanded={open}
        hasPopup="menu"
        onClick={() => setOpen((value) => !value)}
      />
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        placement="top-start"
        width={240}
      >
        <MenuList label="Weitere Aktionen" onClose={() => setOpen(false)}>
          {meeting.guestUrl ? (
            <MenuItem
              label="Link für den Gast kopieren"
              onSelect={() => {
                setOpen(false);
                void copy();
              }}
            />
          ) : null}
          <MenuItem
            label="Transkript"
            onSelect={() => {
              setOpen(false);
              onTranscript();
            }}
          />
        </MenuList>
      </Popover>
    </>
  );
}

function useCopyGuestLink(url: string | null) {
  const { toast } = useToast();
  return async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast({ message: 'Link kopiert' });
    } catch {
      toast({ message: url });
    }
  };
}

function EndButton({
  onEnd,
  ending,
  recording,
  compact = false,
  round = false,
}: {
  onEnd: () => Promise<void>;
  ending: boolean;
  /** The meeting records: the dialog says the recording ends. */
  recording: boolean;
  compact?: boolean;
  round?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {round ? (
        <RoundControl
          label="Ende"
          ariaLabel="Ende"
          tone="danger"
          size="large"
          onClick={() => setOpen(true)}
        />
      ) : (
        <PillControl tone="danger" compact={compact} onClick={() => setOpen(true)}>
          Beenden
        </PillControl>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Gespräch beenden?"
        width={560}
        overlayTop={0}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button variant="destructiveFilled" loading={ending} onClick={() => void onEnd()}>
              Beenden
            </Button>
          </>
        }
      >
        <p className={styles.dialogText}>
          {recording
            ? 'Die Aufzeichnung endet, der Gast verlässt den Meetingraum. Danach öffnet sich der Rückblick.'
            : 'Das Gespräch endet, der Gast verlässt den Meetingraum. Danach öffnet sich der Rückblick.'}
        </p>
      </Dialog>
    </>
  );
}

type MobileSheet = 'note' | 'transcript' | 'marker' | null;

function MobileCall(props: CallViewProps) {
  const { meeting, connection } = props;
  const facts = useCallFacts(props);
  const [sheet, setSheet] = useState<MobileSheet>(null);
  const markers = useMarkers(meeting.id);
  const sharing = props.shareLabel !== null;
  const deal = meeting.context.deal;
  const openCount = meeting.context.openTasks.filter((task) => !task.done).length;
  const factsLine = deal
    ? [deal.title?.split(' · ')[0] ?? null, deal.area, deal.purchasePrice].filter(Boolean).join(' · ')
    : (meeting.appointment.contactName ?? facts.guestName);
  const stageLine = `${deal ? `${deal.stage} · ` : ''}${openCount} offene ${openCount === 1 ? 'Aufgabe' : 'Aufgaben'}`;
  const mainTrack = sharing ? (facts.local?.screenTrack ?? null) : (facts.primary?.videoTrack ?? null);

  return (
    <div className={styles.mobilePage}>
      <RemoteAudio tracks={facts.audioTracks} />
      <ClockAnnouncement seconds={facts.seconds} recording={facts.recording} />
      <h1 className={styles.srOnly}>
        {meeting.appointment.eventTypeName} · {shortPersonName(facts.guestName)}
      </h1>
      <div className={styles.mobilePills}>
        {facts.recording ? (
          <RecordingPill>
            <span className={styles.srOnly}>Aufzeichnung </span>
            {formatClock(facts.seconds)}
          </RecordingPill>
        ) : (
          <ConversationPill>Gespräch · {formatClock(facts.seconds)}</ConversationPill>
        )}
        {sharing ? <span className={styles.mobileDeal}>Sie teilen: {props.shareLabel}</span> : null}
        {!sharing && facts.dealShort ? <span className={styles.mobileDeal}>{facts.dealShort}</span> : null}
      </div>
      <div className={styles.mobileStage}>
        <ConnectionNotice connection={connection} onReconnect={props.onReconnect} />
        <div className={styles.mobileVideo}>
          <VideoTile
            className={styles.mobileMain}
            track={mainTrack}
            name={
              sharing
                ? `Sie teilen: ${props.shareLabel ?? ''}`
                : `${shortPersonName(facts.primary?.name ?? facts.guestName)}${facts.primary?.speaking ? ' · spricht' : ''}`
            }
            active={!sharing && (facts.primary?.speaking ?? false)}
            placeholder={
              connection.state === 'connecting'
                ? 'Verbindung wird aufgebaut …'
                : `${facts.guestName} ist noch nicht im Gespräch.`
            }
          />
          <div className={styles.pip}>
            {facts.local?.videoTrack ? <TrackVideo track={facts.local.videoTrack} mirrored /> : null}
            <span className={styles.srOnly}>Sie</span>
          </div>
        </div>
        <section className={styles.contextCard} aria-label="Kontext">
          <div className={styles.eyebrow}>Kontext</div>
          <div className={styles.contextFacts}>{factsLine}</div>
          <div className={styles.contextSub}>{stageLine}</div>
          <div className={styles.contextPills}>
            <button type="button" className={styles.contextPill} onClick={() => setSheet('note')}>
              Notiz
            </button>
            <button type="button" className={styles.contextPill} onClick={() => setSheet('transcript')}>
              Transkript
            </button>
            <button
              type="button"
              className={styles.contextPill}
              disabled={!canMark(meeting)}
              onClick={() => setSheet('marker')}
            >
              Markieren
            </button>
          </div>
        </section>
      </div>
      <div className={styles.mobileControls} role="toolbar" aria-label="Gespräch">
        <MicCam {...props} large />
        {sharing ? (
          <RoundControl
            label="Teilen"
            ariaLabel="Freigabe beenden"
            tone="accent"
            size="large"
            pressed
            onClick={() => void props.onStopShare()}
          />
        ) : (
          <ShareButton label="Teilen" tone="accent" onShare={props.onShare} round />
        )}
        <EndButton onEnd={props.onEnd} ending={props.ending} recording={facts.recording} round />
      </div>

      <Sheet
        open={sheet === 'note'}
        onClose={() => setSheet(null)}
        title="Notiz"
        className={styles.darkSheet}
      >
        <NoteForm meeting={meeting} />
      </Sheet>
      <Sheet
        open={sheet === 'transcript' || sheet === 'marker'}
        onClose={() => setSheet(null)}
        title="Transkript"
        subtitle={markedLabel(markers.count)}
        className={styles.darkSheet}
        height="full"
      >
        <MarkersPanel
          meeting={meeting}
          clockSeconds={facts.clockSeconds}
          startWith={sheet === 'marker' ? 'marker' : undefined}
        />
      </Sheet>
    </div>
  );
}
